import * as ToastPrimitive from "@radix-ui/react-toast";
import React, { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/utils/shadcn-ui";

import styles from "./toasts.module.css";

type ToastVariant = "default" | "info";

interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ShowToastParams {
  message: string;
  variant?: ToastVariant;
}

interface ToastContextValue {
  showToast: (params: ShowToastParams) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastsProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToastFn = ({ message, variant = "default" }: ShowToastParams) => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { id, message, variant }]);
  };

  // Connect the global toast function
  useEffect(() => {
    setGlobalToast(showToastFn);
    return () => {
      setGlobalToast(() => {});
    };
  }, []);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast: showToastFn }}>
      {children}
      <ToastPrimitive.Provider>
        {createPortal(
          <ToastPrimitive.Viewport className={styles.list} />,
          document.body,
        )}
        {toasts.map((toast) => (
          <ToastPrimitive.Root
            key={toast.id}
            className={cn(
              styles.toast,
              toast.variant === "info" && styles.toastInfo,
            )}
            onOpenChange={(open) => {
              if (!open) removeToast(toast.id);
            }}
          >
            <ToastPrimitive.Title>{toast.message}</ToastPrimitive.Title>
            <div className="grow" />
            <ToastPrimitive.Close className={styles.closeBtn}>
              X
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastsProvider");
  }
  return context;
}

// Backwards compatibility - standalone component (deprecated)
export function Toasts() {
  return null; // In React version, toasts are handled by ToastsProvider
}

// Global toast function for backwards compatibility
let globalShowToast: ((params: ShowToastParams) => void) | null = null;

export function setGlobalToast(fn: (params: ShowToastParams) => void) {
  globalShowToast = fn;
}

export function showToast(params: ShowToastParams) {
  if (globalShowToast) {
    globalShowToast(params);
  } else {
    console.warn("Toast provider not initialized. Wrap app in ToastsProvider.");
  }
}
